/**
 * 消息加工逻辑全在 MessageHandler，职责单一
 * 消息处理器：负责分类和处理不同类型的WebSocket消息
 * 收到消息: { type: 'NOTIFICATION', destination: '/topic/orders', payload: {...} }

* 第1级：查 "NOTIFICATION:/topic/orders"  ← 精确匹配（特定类型+特定主题）
   ↓ 没命中
* 第2级：查 "NOTIFICATION"                ← 通用匹配（只看类型）
   ↓ 没命中
* 兜底：defaultHandler                    ← 返回 { type: 'unhandled', ... }

* NOTIFICATION	/topic/orders	NOTIFICATION:/topic/orders
* NOTIFICATION	null	NOTIFICATION
 */
class MessageHandler {
  constructor() {
    // 消息类型映射表，key为消息类型+目标地址，value为处理函数  java的map一样用法
    this.handlerMap = new Map();
    
    // 初始化默认处理器
    this.initDefaultHandlers();
  }
  
  /**
   * 初始化默认消息处理器
   */
  initDefaultHandlers() {
    // 系统通知消息处理器
    this.registerHandler('SYSTEM_NOTIFICATION', null, (payload) => {
      // 可以在这里统一处理系统通知，如显示全局提示
      return {
        type: 'system',
        data: payload
      };
    });
    
    // 错误消息处理器
    this.registerHandler('ERROR', null, (payload) => {
      console.error('错误消息:', payload);
      return {
        type: 'error',
        data: payload
      };
    });

    // 心跳消息处理器  什么也不做
    this.registerHandler('HEARTBEAT', null, (payload, message) => {
      // 这里只做轻量处理，不执行业务逻辑
      return {
        type: 'heartbeat',
        destination: message.destination,
        timestamp: message.timestamp
      };
    });
  }
  
  /**
   * 注册消息处理器
   * @param {string} messageType - 消息类型
   * @param {string} destination - 目标主题(可选)
   * @param {function} handler - 处理函数
   */
  registerHandler(messageType, destination, handler) {
    // 生成唯一键：消息类型+目标地址
    const key = this.generateKey(messageType, destination);
    if (typeof handler === 'function') {
      this.handlerMap.set(key, handler);
    } else {
      console.error('消息处理器必须是函数');
    }
  }
  
  /**
   * 处理消息
   * @param {object} message - 原始消息对象
   * @returns {object} 处理后的消息
   */
  handleMessage(message) {
    if (!message || !message.type) {
      console.error('无效的消息格式:', message);
      return null;
    }
    
    // 尝试匹配精确的处理器（类型+目标）
    let key = this.generateKey(message.type, message.destination);
    let handler = this.handlerMap.get(key);
    
    // 如果没有精确匹配，尝试只匹配类型
    if (!handler) {
      key = this.generateKey(message.type, null);
      handler = this.handlerMap.get(key);
    }
    
    // 如果有处理器则使用处理器处理，否则使用默认处理
    if (handler) {
      try {
        return handler(message.payload, message);
      } catch (error) {
        console.error(`处理消息失败 (${key}):`, error);
        return this.defaultHandler(message);
      }
    } else {
      return this.defaultHandler(message);
    }
  }
  
  /**
   * 默认消息处理器
   * @param {object} message - 原始消息对象
   * @returns {object} 处理后的消息
   */
  defaultHandler(message) {
    return {
      type: 'unhandled',
      originalType: message.type,
      destination: message.destination,
      data: message.payload
    };
  }
  
  /**
   * 生成处理器映射键  默认destination 设置为null，只匹配类型
   * @param {string} messageType - 消息类型
   * @param {string} destination - 目标主题
   * @returns {string} 映射键
   */
  generateKey(messageType, destination) {
    if (destination) {
      return messageType + ':' + destination;
    }
    return messageType;
  }
  
  /**
   * 移除消息处理器
   * @param {string} messageType - 消息类型
   * @param {string} destination - 目标主题(可选)
   */
  removeHandler(messageType, destination) {
    const key = this.generateKey(messageType, destination);
    this.handlerMap.delete(key);
  }
}

// 导出单例实例
export default new MessageHandler();
